/* eslint-disable no-undef */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from 'react'
import type { Map, Marker } from 'mapbox-gl'
import { useRouter } from '@popety-io/ui'
import { useDispatch } from 'react-redux'
import type { BasemapStyle, ViewMode } from '../types/mapContext.types'
import type { LayerType } from '../types'
import type { Land, AggregatedLandData } from '@popety-io/types'
import { layerStorage } from '../Lands/utils'
import { setActiveWMSLayer } from '@/store/slices/wmsLayersSlice'

export interface MapContextValue {
  // Map state
  map: Map | null
  mapLoaded: boolean
  basemap: BasemapStyle
  activeLayer: LayerType | null
  transparency: number
  favoriteLayers: LayerType[]
  searchMarker: Marker | null

  // View state
  viewMode: ViewMode

  // Panel state
  layersPanelOpen: boolean
  legendPanelOpen: boolean
  filterPanelOpen: boolean
  propertyPanelOpen: boolean
  selectedPropertyId: string | null
  selectedTileData: Land | null

  // Group mode state
  groupModeActive: boolean
  selectedLandIds: string[]
  groupAggregatedData: AggregatedLandData | null
  groupGeometry: GeoJSON.Feature | null
  groupValidated: boolean
  groupingId: string | null

  // Map actions
  setMap: (map: Map | null) => void
  setMapLoaded: (loaded: boolean) => void
  setBasemap: (style: BasemapStyle) => void
  setActiveLayer: (layer: LayerType | null) => void
  setTransparency: (value: number) => void
  toggleFavoriteLayer: (layerId: LayerType) => void
  setSearchMarker: (marker: Marker | null) => void
  removeSearchMarker: () => void

  // View actions
  setViewMode: (mode: ViewMode) => void

  // Panel actions
  setLayersPanelOpen: (open: boolean) => void
  setLegendPanelOpen: (open: boolean) => void
  setFilterPanelOpen: (open: boolean) => void
  setPropertyPanelOpen: (open: boolean) => void
  setSelectedPropertyId: (id: string | null) => void
  setSelectedTileData: (land: Land | null) => void
  openPropertyPanel: (propertyId: string) => void
  closePropertyPanel: () => void

  // Group mode actions
  activateGroupMode: () => void
  deactivateGroupMode: () => void
  addLandToGroup: (landId: string) => void
  removeLandFromGroup: (landId: string) => void
  setGroupAggregatedData: (data: AggregatedLandData | null) => void
  setGroupGeometry: (geometry: GeoJSON.Feature | null) => void
  setGroupValidated: (validated: boolean) => void
  setGroupingId: (id: string | null) => void
  clearGroup: () => void
}

const MapContext = createContext<MapContextValue | undefined>(undefined)

interface MapProviderProps {
  children: ReactNode
}

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  const { query, updateQuery } = useRouter()
  const {
    basemap: basemapQuery,
    view: viewQuery,
    layer: layerQuery,
    mode: modeQuery,
  } = query
  const dispatch = useDispatch()

  // Get current mode
  const currentMode = (modeQuery as 'plot' | 'building') || 'plot'
  const previousMode = useRef<'plot' | 'building'>(currentMode)

  // Track intentional panel closes to prevent race condition with URL updates
  const isClosingPanelRef = useRef(false)

  // Initialize state from localStorage and URL
  const [map, setMap] = useState<Map | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [searchMarker, setSearchMarker] = useState<Marker | null>(null)
  const [basemap, setBasemapState] = useState<BasemapStyle>(
    (basemapQuery as BasemapStyle) || 'street'
  )
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // Priority: URL query > localStorage > default
    if (viewQuery && (viewQuery === 'map' || viewQuery === 'list')) {
      return viewQuery as ViewMode
    }
    return layerStorage.getViewMode()
  })
  const [activeLayer, setActiveLayerState] = useState<LayerType | null>(() => {
    // Priority: URL query > mode-specific localStorage > fallback to general localStorage > default
    if (layerQuery) {
      // If it's a WMS layer, don't set it as activeLayer (it will be handled by WMS system)
      if (typeof layerQuery === 'string' && layerQuery.startsWith('wms_')) {
        return null
      }
      return layerQuery as LayerType
    }
    // Try mode-specific storage first, then fallback to general storage
    const initialMode = (modeQuery as 'plot' | 'building') || 'plot'
    return (
      layerStorage.getActiveLayerForMode(initialMode) ||
      layerStorage.getActiveLayer()
    )
  })
  const [transparency, setTransparencyState] = useState(() =>
    layerStorage.getTransparency()
  )
  const [layersPanelOpen, setLayersPanelOpen] = useState(false)
  const [legendPanelOpen, setLegendPanelOpen] = useState(false)
  const [filterPanelOpen, setFilterPanelOpen] = useState(true)
  const [propertyPanelOpen, setPropertyPanelOpen] = useState(false)
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    null
  )
  const [selectedTileData, setSelectedTileData] = useState<Land | null>(null)
  const [favoriteLayers, setFavoriteLayers] = useState<LayerType[]>(() => {
    const stored = layerStorage.getFavoriteLayers()
    return stored.length > 0
      ? stored
      : [
          'development_score' as LayerType,
          'restriction_type' as LayerType,
          'day_noise_street' as LayerType,
        ]
  })

  // Group mode state
  const [groupModeActive, setGroupModeActive] = useState(false)
  const [selectedLandIds, setSelectedLandIds] = useState<string[]>([])
  const [groupAggregatedData, setGroupAggregatedData] =
    useState<AggregatedLandData | null>(null)
  const [groupGeometry, setGroupGeometry] = useState<GeoJSON.Feature | null>(
    null
  )
  const [groupValidated, setGroupValidated] = useState(false)
  const [groupingId, setGroupingId] = useState<string | null>(null)

  // Wrapped setters that also update localStorage/URL
  const setBasemap = useCallback(
    (style: BasemapStyle) => {
      setBasemapState(style)
      updateQuery({ basemap: style })
    },
    [updateQuery]
  )

  const setActiveLayer = useCallback(
    (layer: LayerType | null) => {
      setActiveLayerState(layer)
      // Save to both mode-specific and general storage for backward compatibility
      layerStorage.setActiveLayerForMode(currentMode, layer)
      layerStorage.setActiveLayer(layer)
      // Update URL if needed (but don't override explicit URL layer parameter)
      if (!layerQuery) {
        updateQuery({ layer: layer || undefined })
      }
    },
    [currentMode, layerQuery, updateQuery]
  )

  const setTransparency = useCallback((value: number) => {
    setTransparencyState(value)
    layerStorage.setTransparency(value)
  }, [])

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setViewModeState(mode)
      layerStorage.setViewMode(mode)
      updateQuery({ view: mode })
    },
    [updateQuery]
  )

  const toggleFavoriteLayer = useCallback((layerId: LayerType) => {
    setFavoriteLayers((prev) => {
      const updated = prev.includes(layerId)
        ? prev.filter((id) => id !== layerId)
        : [...prev, layerId]
      layerStorage.setFavoriteLayers(updated)
      return updated
    })
  }, [])

  // Search marker functions
  const removeSearchMarker = useCallback(() => {
    if (searchMarker) {
      searchMarker.remove()
      setSearchMarker(null)
    }
  }, [searchMarker])

  // Property panel functions
  const openPropertyPanel = useCallback(
    (propertyId: string) => {
      setSelectedPropertyId(propertyId)
      setPropertyPanelOpen(true)

      // Add landId or buildingId to URL based on current mode
      // Property IDs are managed via direct updateQuery (not batched)
      const newQuery = { ...query }
      if (currentMode === 'plot') {
        newQuery.landId = propertyId
        // Remove buildingId if present
        delete newQuery.buildingId
      } else {
        newQuery.buildingId = propertyId
        // Remove landId if present
        delete newQuery.landId
      }
      updateQuery(newQuery)
    },
    [currentMode, updateQuery, query]
  )

  const closePropertyPanel = useCallback(() => {
    // Mark as intentional close to prevent race condition with URL sync
    isClosingPanelRef.current = true

    setPropertyPanelOpen(false)
    setSelectedPropertyId(null)
    setSelectedTileData(null)

    // Remove landId and buildingId from URL
    // Property IDs are managed via direct updateQuery (not batched)
    const newQuery = { ...query }
    delete newQuery.landId
    delete newQuery.buildingId
    updateQuery(newQuery)

    // Reset flag after URL update completes (React Router navigation is async)
    setTimeout(() => {
      isClosingPanelRef.current = false
    }, 100)
  }, [updateQuery, query])

  // Group mode functions
  const activateGroupMode = useCallback(() => {
    setGroupModeActive(true)
    // Add currently selected land to group if one is selected
    if (selectedPropertyId) {
      setSelectedLandIds([selectedPropertyId])
    }
  }, [selectedPropertyId])

  const deactivateGroupMode = useCallback(() => {
    setGroupModeActive(false)
    setSelectedLandIds([])
    setGroupAggregatedData(null)
    setGroupGeometry(null)
    setGroupValidated(false)
    setGroupingId(null)
  }, [])

  const addLandToGroup = useCallback((landId: string) => {
    setSelectedLandIds((prev) => {
      if (prev.includes(landId)) {
        return prev
      }
      return [...prev, landId]
    })
    // Reset validation when adding new land
    setGroupValidated(false)
    setGroupingId(null)
  }, [])

  const removeLandFromGroup = useCallback((landId: string) => {
    setSelectedLandIds((prev) => prev.filter((id) => id !== landId))
    // Reset validation when removing land
    setGroupValidated(false)
    setGroupingId(null)
  }, [])

  const clearGroup = useCallback(() => {
    setSelectedLandIds([])
    setGroupAggregatedData(null)
    setGroupGeometry(null)
    setGroupValidated(false)
    setGroupingId(null)
    setGroupModeActive(false)
  }, [])

  // Handle mode changes - restore the last selected layer for the new mode
  useEffect(() => {
    // Only trigger when mode actually changes (not on initial load)
    if (previousMode.current !== currentMode) {
      // Save current layer to previous mode storage before switching
      if (activeLayer) {
        layerStorage.setActiveLayerForMode(previousMode.current, activeLayer)
      }

      // Clear property panel and remove inappropriate ID from URL when switching modes
      const newQuery = { ...query }
      delete newQuery.layer
      if (currentMode === 'plot') {
        // Switching to plot mode - remove buildingId if present
        delete newQuery.buildingId
      } else {
        // Switching to building mode - remove landId if present
        delete newQuery.landId
      }
      updateQuery(newQuery)

      setPropertyPanelOpen(false)
      setSelectedPropertyId(null)
      setSelectedTileData(null)

      // Always restore layer for new mode
      const modeSpecificLayer = layerStorage.getActiveLayerForMode(currentMode)
      // Always set the mode-specific layer, even if it's the same as current
      setActiveLayerState(modeSpecificLayer)
      // Also update general storage for backward compatibility
      if (modeSpecificLayer) {
        layerStorage.setActiveLayer(modeSpecificLayer)
      }

      // Update ref to current mode
      previousMode.current = currentMode
    }
  }, [currentMode, updateQuery, activeLayer, query])

  // Handle WMS layer initialization from URL
  useEffect(() => {
    if (
      layerQuery &&
      typeof layerQuery === 'string' &&
      layerQuery.startsWith('wms_')
    ) {
      const wmsLayerId = layerQuery.replace('wms_', '')
      dispatch(setActiveWMSLayer({ layerId: wmsLayerId }))
      // Clear regular layer selection
      setActiveLayerState(null)
    }
  }, [layerQuery, dispatch])

  // Handle property panel initialization from URL
  useEffect(() => {
    // Don't reopen if we just closed intentionally (prevents race condition)
    if (isClosingPanelRef.current) return

    const landId = query.landId as string | undefined
    const buildingId = query.buildingId as string | undefined

    // Open property panel if landId or buildingId is in URL
    if (currentMode === 'plot' && landId && landId !== selectedPropertyId) {
      setSelectedPropertyId(landId)
      setPropertyPanelOpen(true)
    } else if (
      currentMode === 'building' &&
      buildingId &&
      buildingId !== selectedPropertyId
    ) {
      setSelectedPropertyId(buildingId)
      setPropertyPanelOpen(true)
    }
  }, [query.landId, query.buildingId, currentMode, selectedPropertyId])

  // Cleanup search marker on unmount
  useEffect(() => {
    return () => {
      if (searchMarker) {
        searchMarker.remove()
      }
    }
  }, [searchMarker])

  const value: MapContextValue = {
    map,
    mapLoaded,
    basemap,
    activeLayer,
    transparency,
    viewMode,
    layersPanelOpen,
    legendPanelOpen,
    filterPanelOpen,
    propertyPanelOpen,
    selectedPropertyId,
    selectedTileData,
    favoriteLayers,
    searchMarker,
    groupModeActive,
    selectedLandIds,
    groupAggregatedData,
    groupGeometry,
    groupValidated,
    groupingId,
    setMap,
    setMapLoaded,
    setBasemap,
    setActiveLayer,
    setTransparency,
    setViewMode,
    setLayersPanelOpen,
    setLegendPanelOpen,
    setFilterPanelOpen,
    setPropertyPanelOpen,
    setSelectedPropertyId,
    setSelectedTileData,
    openPropertyPanel,
    closePropertyPanel,
    toggleFavoriteLayer,
    setSearchMarker,
    removeSearchMarker,
    activateGroupMode,
    deactivateGroupMode,
    addLandToGroup,
    removeLandFromGroup,
    setGroupAggregatedData,
    setGroupGeometry,
    setGroupValidated,
    setGroupingId,
    clearGroup,
  }

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>
}

export const useMap = () => {
  const context = useContext(MapContext)
  if (!context) {
    throw new Error('useMap must be used within a MapProvider')
  }
  return context
}
